<template>
  <div class="os-breakdown">
    <div class="os-breakdown__header">
      <h3 class="os-breakdown__title">{{ title }}</h3>
    </div>

    <div class="os-breakdown__metrics">
      <button
        v-for="m in metrics"
        :key="m.key"
        type="button"
        :class="['os-breakdown__pill', { 'is-active': activeMetric === m.key }]"
        @click="activeMetric = m.key"
      >{{ m.label }}</button>
    </div>

    <div class="os-breakdown__body">
      <!-- 左カラム: ドーナツ または 横棒 -->
      <div class="os-breakdown__chart-col">
        <!-- ドーナツ (表示数/クリック数) -->
        <div v-if="!isRatio" class="os-breakdown__donut-wrap">
          <svg :viewBox="donutViewBox" class="os-breakdown__svg">
            <g :transform="`translate(${svgSize/2},${svgSize/2})`">
              <!-- 内側リング: OSカテゴリ集計 -->
              <path
                v-for="(seg, i) in innerSegments"
                :key="'g-' + i"
                :d="seg.path"
                :fill="seg.color"
                fill-opacity="0.55"
                stroke="#ffffff"
                stroke-width="2"
                class="os-breakdown__segment"
                @mousemove="onHover($event, seg)"
                @mouseleave="onLeave"
              />
              <!-- 外側リング: OS別 -->
              <path
                v-for="(seg, i) in outerSegments"
                :key="'o-' + i"
                :d="seg.path"
                :fill="seg.color"
                stroke="#ffffff"
                stroke-width="2"
                class="os-breakdown__segment"
                @mousemove="onHover($event, seg)"
                @mouseleave="onLeave"
              />
              <!-- 外側ラベル (6%以上のみ) -->
              <template v-for="(seg, i) in outerSegments" :key="'l-' + i">
                <text
                  v-if="seg.percent >= 0.06"
                  :x="seg.labelX"
                  :y="seg.labelY"
                  text-anchor="middle"
                  dominant-baseline="central"
                  class="os-breakdown__segment-label"
                >{{ Math.round(seg.percent * 100) }}%</text>
              </template>
            </g>
          </svg>
          <div class="os-breakdown__donut-center">
            <div class="os-breakdown__donut-center-label">合計</div>
            <div class="os-breakdown__donut-center-value">{{ formatValue(totalValue) }}</div>
            <div class="os-breakdown__donut-center-sub">{{ currentMetric.label }}</div>
          </div>
          <div class="os-breakdown__donut-legend">
            <span>内側: OSカテゴリ</span>
            <span>外側: OS別</span>
          </div>
        </div>

        <!-- 横棒 (CTR) -->
        <div v-else class="os-breakdown__ctr">
          <svg :viewBox="`0 0 ${barWidth} ${barHeight}`" class="os-breakdown__svg" preserveAspectRatio="xMidYMid meet">
            <g>
              <line
                v-for="g in gridLines"
                :key="'g-' + g.x"
                :x1="g.x"
                :x2="g.x"
                :y1="0"
                :y2="barInnerHeight"
                stroke="#e0edf0"
                stroke-dasharray="2 4"
              />
              <text
                v-for="g in gridLines"
                :key="'gt-' + g.x"
                :x="g.x"
                :y="barInnerHeight + 14"
                text-anchor="middle"
                class="os-breakdown__axis-text"
              >{{ g.label }}%</text>
            </g>
            <g v-for="bar in barItems" :key="bar.name">
              <text
                :x="barLabelWidth - 8"
                :y="bar.y + bar.height / 2"
                text-anchor="end"
                dominant-baseline="central"
                class="os-breakdown__bar-name"
              >{{ bar.name }}</text>
              <rect
                :x="barLabelWidth"
                :y="bar.y"
                :width="bar.width"
                :height="bar.height"
                :fill="bar.color"
                rx="2"
                ry="2"
                class="os-breakdown__segment"
                @mousemove="onHover($event, bar)"
                @mouseleave="onLeave"
              />
              <text
                :x="barLabelWidth + bar.width + 6"
                :y="bar.y + bar.height / 2"
                dominant-baseline="central"
                class="os-breakdown__bar-value"
              >{{ formatValue(bar.value) }}%</text>
            </g>
          </svg>
        </div>
      </div>

      <!-- 右カラム: グループ化された内訳 (全モード共通) -->
      <div class="os-breakdown__list">
        <div class="os-breakdown__list-heading">内訳</div>
        <div v-for="group in groupedList" :key="group.name" class="os-breakdown__group">
          <div class="os-breakdown__group-header">
            <span class="os-breakdown__group-title" :style="{ color: group.color }">
              <span class="os-breakdown__group-dot" :style="{ background: group.color }"></span>
              {{ group.name }}
            </span>
            <span class="os-breakdown__group-total">
              <template v-if="isRatio">{{ formatValue(group.weightedCtr) }}%</template>
              <template v-else>{{ formatValue(group.total) }}<span class="os-breakdown__group-percent"> ({{ percentOf(group.total) }}%)</span></template>
            </span>
          </div>
          <div v-for="item in group.items" :key="item.name" class="os-breakdown__item">
            <span class="os-breakdown__item-swatch" :style="{ background: item.color }"></span>
            <span class="os-breakdown__item-name">{{ item.name }}</span>
            <span class="os-breakdown__item-value">
              {{ formatValue(item.value) }}<template v-if="isRatio">%</template><span v-if="!isRatio" class="os-breakdown__item-percent"> ({{ percentOf(item.value) }}%)</span>
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- ツールチップ -->
    <div
      v-if="tooltip.visible"
      class="os-breakdown__tooltip"
      :style="{ left: tooltip.x + 'px', top: tooltip.y + 'px' }"
    >
      <div class="os-breakdown__tooltip-title" :style="{ color: tooltip.color }">{{ tooltip.name }}</div>
      <div class="os-breakdown__tooltip-row">
        <span>{{ currentMetric.label }}</span>
        <span class="os-breakdown__tooltip-value">{{ formatValue(tooltip.value) }}{{ currentMetric.unit }}</span>
      </div>
    </div>
  </div>
</template>

<script>
// OS → 所属カテゴリ & 固有色の対応表
// デスクトップ系はダーク寄りティール、モバイル系はライト寄りシアン、その他はニュートラルなグレーティール
const OS_GROUPS = {
  'Windows': { group: 'デスクトップ', color: '#209bb9' },
  'Mac':     { group: 'デスクトップ', color: '#4cb5cc' },
  'iOS':     { group: 'モバイル',     color: '#3cb6d2' },
  'Android': { group: 'モバイル',     color: '#6ac9de' },
  'その他':  { group: 'その他',       color: '#9dafb4' },
};

const GROUP_COLOR = {
  'デスクトップ': '#209bb9',
  'モバイル':     '#3cb6d2',
  'その他':       '#9dafb4',
};

const GROUP_ORDER = { 'デスクトップ': 0, 'モバイル': 1, 'その他': 2 };

export default {
  props: {
    data: { type: Array, default: function () { return []; } },
    nameKey: { type: String, default: 'os_name' },
    title: { type: String, default: 'OS別集計' },
  },
  data: function () {
    return {
      activeMetric: 'display_count',
      metrics: [
        { key: 'display_count', label: '表示数',     unit: '' },
        { key: 'click_count',   label: 'クリック数', unit: '' },
        { key: 'ctr',           label: 'CTR',        unit: '%' },
      ],
      svgSize: 260,
      barWidth: 420,
      barHeight: 280,
      barLabelWidth: 80,
      barBottomPad: 30,
      tooltip: { visible: false, x: 0, y: 0, name: '', value: 0, color: '#333' },
    };
  },
  computed: {
    currentMetric: function () {
      var self = this;
      return this.metrics.find(function (m) { return m.key === self.activeMetric; });
    },
    isRatio: function () {
      return this.activeMetric === 'ctr';
    },
    donutViewBox: function () {
      return '0 0 ' + this.svgSize + ' ' + this.svgSize;
    },
    sortedData: function () {
      var self = this;
      var arr = (this.data || []).slice();
      arr.sort(function (a, b) {
        var ga = self.groupOrderOf(a[self.nameKey]);
        var gb = self.groupOrderOf(b[self.nameKey]);
        return ga - gb;
      });
      return arr;
    },
    itemsWithValue: function () {
      var self = this;
      return this.sortedData.map(function (d) {
        var name = d[self.nameKey];
        var info = OS_GROUPS[name] || { group: 'その他', color: '#9dafb4' };
        return {
          name: name,
          value: self.metricValueOf(d),
          group: info.group,
          color: info.color,
          displayCount: Number(d.display_count || 0),
          clickCount: Number(d.click_count || 0),
        };
      });
    },
    totalValue: function () {
      if (this.isRatio) return null;
      return this.itemsWithValue.reduce(function (s, d) { return s + (d.value || 0); }, 0);
    },
    groupTotals: function () {
      var map = {};
      this.itemsWithValue.forEach(function (it) {
        if (!map[it.group]) map[it.group] = 0;
        map[it.group] += (it.value || 0);
      });
      return Object.keys(map)
        .sort(function (a, b) {
          var oa = GROUP_ORDER[a]; var ob = GROUP_ORDER[b];
          return (oa === undefined ? 99 : oa) - (ob === undefined ? 99 : ob);
        })
        .map(function (name) {
          return { name: name, value: map[name], color: GROUP_COLOR[name] || '#9dafb4' };
        });
    },
    innerSegments: function () {
      return this.buildSegments(this.groupTotals, 40, 68);
    },
    outerSegments: function () {
      return this.buildSegments(this.itemsWithValue, 72, 115);
    },
    groupedList: function () {
      var groups = {};
      this.itemsWithValue.forEach(function (it) {
        if (!groups[it.group]) {
          groups[it.group] = {
            name: it.group,
            color: GROUP_COLOR[it.group] || '#9dafb4',
            items: [],
            total: 0,
            displayCount: 0,
            clickCount: 0,
            weightedCtr: 0,
          };
        }
        groups[it.group].items.push(it);
        groups[it.group].total += (it.value || 0);
        groups[it.group].displayCount += (it.displayCount || 0);
        groups[it.group].clickCount += (it.clickCount || 0);
      });
      Object.keys(groups).forEach(function (k) {
        var g = groups[k];
        g.weightedCtr = g.displayCount > 0 ? (g.clickCount * 100 / g.displayCount) : 0;
      });
      return Object.keys(groups)
        .sort(function (a, b) {
          var oa = GROUP_ORDER[a]; var ob = GROUP_ORDER[b];
          return (oa === undefined ? 99 : oa) - (ob === undefined ? 99 : ob);
        })
        .map(function (k) { return groups[k]; });
    },
    barInnerHeight: function () {
      return this.barHeight - this.barBottomPad;
    },
    barMaxValue: function () {
      var max = 0;
      this.itemsWithValue.forEach(function (d) { if ((d.value || 0) > max) max = d.value; });
      return this.niceCeil(max);
    },
    barItems: function () {
      var self = this;
      var rows = this.itemsWithValue;
      var innerWidth = this.barWidth - this.barLabelWidth - 60;
      var rowHeight = this.barInnerHeight / Math.max(rows.length, 1);
      var barThickness = Math.min(28, rowHeight * 0.55);
      return rows.map(function (d, i) {
        var w = self.barMaxValue > 0 ? (d.value / self.barMaxValue) * innerWidth : 0;
        return {
          name: d.name,
          value: d.value,
          color: d.color,
          y: i * rowHeight + (rowHeight - barThickness) / 2,
          height: barThickness,
          width: w,
        };
      });
    },
    gridLines: function () {
      var self = this;
      var innerWidth = this.barWidth - this.barLabelWidth - 60;
      var ticks = 5;
      var lines = [];
      for (var i = 0; i <= ticks; i++) {
        var v = (self.barMaxValue * i) / ticks;
        lines.push({
          x: self.barLabelWidth + (innerWidth * i) / ticks,
          label: v.toFixed(v < 10 ? 1 : 0),
        });
      }
      return lines;
    },
  },
  methods: {
    groupOrderOf: function (name) {
      var info = OS_GROUPS[name];
      if (!info) return 99;
      var o = GROUP_ORDER[info.group];
      return (o === undefined) ? 99 : o;
    },
    metricValueOf: function (row) {
      if (this.activeMetric === 'ctr') {
        var display = Number(row.display_count || 0);
        var click = Number(row.click_count || 0);
        if (display > 0 && click > 0) return click * 100 / display;
        return 0;
      }
      return Number(row[this.activeMetric] || 0);
    },
    buildSegments: function (items, rInner, rOuter) {
      var self = this;
      var total = items.reduce(function (s, d) { return s + (d.value || 0); }, 0);
      if (total <= 0) return [];
      var start = 0;
      var segs = [];
      items.forEach(function (it) {
        var v = it.value || 0;
        if (v <= 0) return;
        var percent = v / total;
        var sweep = percent * 360;
        var end = start + sweep;
        var mid = start + sweep / 2;
        var labelR = rInner + (rOuter - rInner) * 0.55;
        var rad = (mid - 90) * Math.PI / 180;
        segs.push({
          name: it.name,
          value: v,
          percent: percent,
          color: it.color,
          path: self.arcPath(0, 0, rOuter, rInner, start, end),
          labelX: labelR * Math.cos(rad),
          labelY: labelR * Math.sin(rad),
        });
        start = end;
      });
      return segs;
    },
    arcPath: function (cx, cy, rOuter, rInner, startAngle, endAngle) {
      // 360度満杯のケースは2分割で描画してパス崩壊を防ぐ
      var span = endAngle - startAngle;
      if (span >= 360) {
        return this.arcPath(cx, cy, rOuter, rInner, startAngle, startAngle + 180)
          + ' ' + this.arcPath(cx, cy, rOuter, rInner, startAngle + 180, startAngle + 359.999);
      }
      var large = span > 180 ? 1 : 0;
      var toRad = function (a) { return (a - 90) * Math.PI / 180; };
      var sRad = toRad(startAngle);
      var eRad = toRad(endAngle);
      var x1 = cx + rOuter * Math.cos(sRad);
      var y1 = cy + rOuter * Math.sin(sRad);
      var x2 = cx + rOuter * Math.cos(eRad);
      var y2 = cy + rOuter * Math.sin(eRad);
      var x3 = cx + rInner * Math.cos(eRad);
      var y3 = cy + rInner * Math.sin(eRad);
      var x4 = cx + rInner * Math.cos(sRad);
      var y4 = cy + rInner * Math.sin(sRad);
      return [
        'M', x1, y1,
        'A', rOuter, rOuter, 0, large, 1, x2, y2,
        'L', x3, y3,
        'A', rInner, rInner, 0, large, 0, x4, y4,
        'Z',
      ].join(' ');
    },
    niceCeil: function (v) {
      if (!v || v <= 0) return 1;
      var mag = Math.pow(10, Math.floor(Math.log10(v)));
      var norm = v / mag;
      var nice;
      if (norm <= 1) nice = 1;
      else if (norm <= 2) nice = 2;
      else if (norm <= 5) nice = 5;
      else nice = 10;
      return nice * mag;
    },
    formatValue: function (v) {
      if (v === null || v === undefined) return '';
      if (this.isRatio) return Number(v).toFixed(2);
      return Number(v).toLocaleString('ja-JP');
    },
    percentOf: function (v) {
      if (!this.totalValue) return '0.0';
      return ((v / this.totalValue) * 100).toFixed(1);
    },
    onHover: function (evt, seg) {
      var rect = this.$el.getBoundingClientRect();
      this.tooltip = {
        visible: true,
        x: evt.clientX - rect.left + 12,
        y: evt.clientY - rect.top + 12,
        name: seg.name,
        value: seg.value,
        color: seg.color,
      };
    },
    onLeave: function () {
      this.tooltip.visible = false;
    },
  },
};
</script>

<style scoped>
.os-breakdown {
    position: relative;
    background: #ffffff;
    border: 1px solid #cccccc;
    border-radius: 4px;
    padding: 20px 24px 24px;
    margin-bottom: 20px;
    font-family: "Hiragino Kaku Gothic ProN", "Noto Sans JP", Meiryo, sans-serif;
    color: #333333;
}

.os-breakdown__header {
    padding-bottom: 10px;
    margin-bottom: 16px;
    border-bottom: 2px solid #3cb6d2;
}

.os-breakdown__title {
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    color: #209bb9;
    letter-spacing: 0.02em;
}

.os-breakdown__metrics {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 20px;
}

.os-breakdown__pill {
    padding: 6px 18px;
    font-size: 13px;
    font-weight: 700;
    border: 1px solid #cccccc;
    background: #ffffff;
    color: #777777;
    border-radius: 50px;
    cursor: pointer;
    font-family: inherit;
    letter-spacing: 0.02em;
    transition: all 0.15s;
}

.os-breakdown__pill.is-active {
    background: #3cb6d2;
    border-color: #3cb6d2;
    color: #ffffff;
}

.os-breakdown__body {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 24px;
    align-items: center;
    /* デバイス別/OS別/ブラウザ別のタブ切替時にレイアウトシフトが起きないよう、
       もっとも背が高くなる OS別クリック数ビュー (2層ドーナツ + 内訳) に合わせて固定 */
    min-height: 340px;
}

.os-breakdown__donut-wrap {
    position: relative;
    width: 100%;
    aspect-ratio: 1 / 1;
    max-width: 320px;
    margin: 0 auto;
}

.os-breakdown__svg {
    width: 100%;
    height: 100%;
    overflow: visible;
}

.os-breakdown__segment {
    cursor: pointer;
    transition: opacity 0.15s;
}

.os-breakdown__segment:hover {
    opacity: 0.85;
}

.os-breakdown__segment-label {
    font-size: 11px;
    font-weight: 700;
    fill: #ffffff;
    pointer-events: none;
}

.os-breakdown__donut-center {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    text-align: center;
    pointer-events: none;
}

.os-breakdown__donut-center-label {
    font-size: 10px;
    color: #777777;
    font-weight: 700;
    margin-bottom: 1px;
}

.os-breakdown__donut-center-value {
    font-size: 20px;
    font-weight: 700;
    color: #209bb9;
    line-height: 1;
}

.os-breakdown__donut-center-sub {
    font-size: 10px;
    color: #777777;
    margin-top: 2px;
}

.os-breakdown__donut-legend {
    position: absolute;
    left: 50%;
    bottom: -4px;
    transform: translateX(-50%);
    display: flex;
    gap: 12px;
    font-size: 10px;
    color: #777777;
    white-space: nowrap;
}

.os-breakdown__list {
    min-width: 0;
}

.os-breakdown__list-heading {
    font-size: 11px;
    color: #777777;
    font-weight: 700;
    margin-bottom: 8px;
    letter-spacing: 0.05em;
}

.os-breakdown__group {
    margin-bottom: 10px;
}

.os-breakdown__group-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-bottom: 4px;
    border-bottom: 1px dashed #e0edf0;
    margin-bottom: 2px;
}

.os-breakdown__group-title {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.08em;
}

.os-breakdown__group-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    display: inline-block;
}

.os-breakdown__group-total {
    font-size: 11px;
    color: #777777;
    font-weight: 700;
}

.os-breakdown__group-percent {
    font-weight: 400;
}

.os-breakdown__item {
    display: grid;
    grid-template-columns: 14px 1fr auto;
    align-items: center;
    gap: 8px;
    padding: 6px 4px 6px 14px;
    font-size: 12px;
}

.os-breakdown__item-swatch {
    width: 10px;
    height: 10px;
    border-radius: 2px;
    display: inline-block;
}

.os-breakdown__item-name {
    color: #333333;
    font-weight: 700;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.os-breakdown__item-value {
    color: #333333;
    font-weight: 700;
    text-align: right;
}

.os-breakdown__item-percent {
    font-size: 10px;
    color: #777777;
    font-weight: 400;
    margin-left: 4px;
}

.os-breakdown__chart-col {
    min-width: 0;
}

.os-breakdown__ctr {
    width: 100%;
    max-width: 400px;
    margin: 0 auto;
}

.os-breakdown__axis-text {
    font-size: 10px;
    fill: #777777;
}

.os-breakdown__bar-name {
    font-size: 12px;
    fill: #333333;
    font-weight: 700;
}

.os-breakdown__bar-value {
    font-size: 11px;
    fill: #333333;
    font-weight: 700;
}

.os-breakdown__tooltip {
    position: absolute;
    background: #ffffff;
    border: 1px solid #3cb6d2;
    border-radius: 4px;
    padding: 8px 14px;
    box-shadow: 0 2px 10px rgba(32, 155, 185, 0.15);
    font-size: 12px;
    min-width: 140px;
    pointer-events: none;
    z-index: 10;
}

.os-breakdown__tooltip-title {
    font-weight: 700;
    margin-bottom: 4px;
}

.os-breakdown__tooltip-row {
    display: flex;
    justify-content: space-between;
    gap: 12px;
}

.os-breakdown__tooltip-value {
    font-weight: 700;
}

@media (max-width: 900px) {
    .os-breakdown__body {
        grid-template-columns: 1fr;
    }
}
</style>
